import { convertSearchQueryId } from './converterHelpers';
import convertSearchResultConversationToConversationType from './convertSearchResultConversationToConversationType';
import convertSearchResultEventToCalendarItemType from './convertSearchResultEventToCalendarItemType';
import convertSearchResultMessageToMessageType from './convertSearchResultMessageToMessageType';
import createEmptySearchResponse from './createEmptySearchResponse';
import getSearchResultSet from './getSearchResultSet';
import SubstrateSearchRequest from '../../../data/schema/SubstrateSearchRequest';
import CalendarItem from 'owa-service/lib/contract/CalendarItem';
import ConversationType from 'owa-service/lib/contract/ConversationType';
import { isFeatureEnabled } from 'owa-feature-flags';
import ExecuteSearchResponseMessage from 'owa-service/lib/contract/ExecuteSearchResponseMessage';
import Message from 'owa-service/lib/contract/Message';
import SubstrateSearchResponse, {
    SearchResultConversation,
    SearchResultEvent,
    SearchResultMessage,
    SearchResult,
} from '../../../data/schema/SubstrateSearchResponse';

function getDateFromResult<T extends SearchResultMessage | SearchResultConversation>(
    searchResult: SearchResult
): Date {
    const date = (searchResult.Source as T).DateTimeReceived
        ? new Date((searchResult.Source as SearchResultMessage).DateTimeReceived)
        : new Date((searchResult.Source as SearchResultConversation).LastDeliveryTime);
    return date;
}
function sortTopResult<T extends SearchResultMessage | SearchResultConversation>(
    searchResults: SearchResult[]
): SearchResult[] {
    // pull out all conversations that are sorted by relevance
    const topResults = searchResults.filter(result => {
        return (result.Source as T).SortOrderSource === 'Relevance';
    });
    topResults.sort(function(result1, result2) {
        const date1 = getDateFromResult<T>(result1);
        const date2 = getDateFromResult<T>(result2);
        return date2 > date1 ? 0 : -1;
    });
    return topResults;
}
function appendMessages(results: SearchResult[], request: SubstrateSearchRequest): Message[] {
    const messageList: Message[] = [];
    for (const searchResult of results) {
        const messageType = convertSearchResultMessageToMessageType(
            searchResult.Source as SearchResultMessage,
            request
        );
        messageType.InstanceKey = searchResult.ReferenceId;
        messageList.push(messageType);
    }
    return messageList;
}
function appendConversations(
    results: SearchResult[],
    request: SubstrateSearchRequest
): ConversationType[] {
    const conversationList: ConversationType[] = [];
    for (const searchResult of results) {
        const conversationType = convertSearchResultConversationToConversationType(
            searchResult.Source as SearchResultConversation,
            request
        );
        conversationType.InstanceKey = searchResult.ReferenceId;
        conversationList.push(conversationType);
    }
    return conversationList;
}

/**
 * This function converts the 3S response body to the ExecuteSearch response format.
 */
export default function substrateResponseBodyToExecuteSearchResponseBody(
    responseBody: SubstrateSearchResponse,
    request: SubstrateSearchRequest
): ExecuteSearchResponseMessage {
    const resultSet = getSearchResultSet(responseBody);

    /**
     * If result set didn't get returned or if result set is empty return an
     * empty search result set.
     */
    if (!resultSet || !resultSet.Results || resultSet.Results.length === 0) {
        return createEmptySearchResponse();
    }

    const searchResults = resultSet.Results;
    const searchResultsCount = searchResults.length;
    const entitySetType = responseBody.EntitySets[0].Type;
    const messageList: Message[] = [];
    const conversationList: ConversationType[] = [];
    const calendarItemList: CalendarItem[] = [];

    switch (entitySetType) {
        case 'Message':
        case 'Messages':
            if (isFeatureEnabled('sea-sortTopResults')) {
                // pull out all conversations that are sorted by relevance
                // and sort them by datetime
                const topResults = sortTopResult<SearchResultMessage>(searchResults);

                searchResults.splice(0, topResults.length);
                messageList.push(...appendMessages(topResults, request));
            }

            messageList.push(...appendMessages(searchResults, request));
            break;
        case 'Conversation':
        case 'Conversations':
            if (isFeatureEnabled('sea-sortTopResults')) {
                // pull out all conversations that are sorted by relevance
                // and sort them by datetime
                const topResults = sortTopResult<SearchResultConversation>(searchResults);
                searchResults.splice(0, topResults.length);
                conversationList.push(...appendConversations(topResults, request));
            }

            conversationList.push(...appendConversations(searchResults, request));
            break;
        case 'Event':
        case 'Events':
            for (const searchResult of searchResults) {
                const eventType = convertSearchResultEventToCalendarItemType(
                    searchResult.Source as SearchResultEvent
                );
                eventType.InstanceKey = searchResult.ReferenceId;
                calendarItemList.push(eventType);
            }
            break;
        default:
            break;
    }

    return {
        SearchResults: {
            Items: messageList.length > 0 ? messageList : null,
            Conversations: conversationList.length > 0 ? conversationList : null,
            CalendarItems: calendarItemList.length > 0 ? calendarItemList : null,
            MoreResultsAvailable: resultSet.MoreResultsAvailable,
            SearchRefiners: null,
            TotalResultsCount: resultSet.Total,
            SearchTerms: responseBody.SearchTerms,
            SearchResultsCount: searchResultsCount,
            TDQueryId: convertSearchQueryId(resultSet),
        },
    };
}
